import React, { useEffect, useState, useCallback } from 'react';
import MDBox from 'components/MDBox';
import {
  TimeBlockFilters,
  GetTimeBlockHeader,
  getTimeBlockV2Columns,
  getTimeBlockV2ListData,
} from './ManageTimeBlocksV2CommonUtils';
import { useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { intialPageSize, booleanOptions } from 'constants';
import {
  setDataGridCounts,
  resetDataGridCounts,
} from 'utils/commonSlice/dataGridCounts/dataGridCountsSlice';
import DeltaDataGrid from 'utils/CommonDataGrid/CommonDataGrid';
import {
  setTimeBlockV2PreviousKey,
  setTimeBlockV2PaginationKey,
  setTimeBlockV2PaginationData,
  resetTimeBlockV2PaginationData,
} from 'layouts/tutoringV2/store/manageTimeBlocksV2Slice/manageTimeBlocksV2Slice';
import { getV2TimeBlocks } from 'layouts/tutoringV2/services/manageTimeBlocksV2Api';
import ManageTimeBlockDialog from './ManageTimeBlockDialog';

const DEFAULT_TOTAL = 3000;

const DEFAULT_FORM_VALUES = {
  subject: '',
  teacher: '',
  ascending: true,
  activeOnly: true,
};

const INITIAL_TIME_BLOCK_LIST = {
  page: 1,
  rows: [],
  count: 0,
  isLoading: false,
  total: DEFAULT_TOTAL,
  nextPaginationKey: '',
  currentPaginationKey: '',
  pageSize: intialPageSize,
};

const ManageTimeBlocksTabV2 = () => {
  const dispatch = useDispatch();

  const [timeBlockPageKey, setTimeBlockPageKey] = useState('');
  const [openTimeBlockDialog, setOpenTimeBlockDialog] = useState(false);
  const [lastKnownTotal, setLastKnownTotal] = useState(DEFAULT_TOTAL);
  const [timeBlockList, setTimeBlockList] = useState(INITIAL_TIME_BLOCK_LIST);

  const { pageSize, page, nextPaginationKey } = timeBlockList;

  const timeBlocksV2PaginationData = useSelector(
    (state) =>
      state?.timeBlocksV2PaginationDataSlice?.timeBlockV2PaginationData,
  );

  const subjects = useSelector((state) => state?.subjectsV2);
  const availableSubjectsData = subjects?.map((item) => ({
    id: item?.subjectId,
    value: item?.name,
  }));

  const availableUsersList = useSelector((state) => state.availableUsersList);
  const availableUsersData = availableUsersList?.map((item) => ({
    id: item?.userId,
    value: item?.username,
  }));

  const { control, reset, watch } = useForm({
    mode: 'onChange',
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const teacherId = watch('teacher');
  const subjectId = watch('subject');
  const ascending = watch('ascending');
  const activeOnly = watch('activeOnly');

  const resetPagination = useCallback(
    (newPageSize = pageSize) => {
      setLastKnownTotal(DEFAULT_TOTAL);
      setTimeBlockPageKey('');
      setTimeBlockList((prev) => ({
        ...prev,
        page: 1,
        total: DEFAULT_TOTAL,
        pageSize: newPageSize,
        nextPaginationKey: '',
        currentPaginationKey: '',
      }));
      dispatch(resetDataGridCounts());
      dispatch(setDataGridCounts(newPageSize));
      dispatch(resetTimeBlockV2PaginationData());
    },
    [dispatch, pageSize],
  );

  useEffect(() => {
    resetPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    resetPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherId, subjectId, ascending, activeOnly]);

  useEffect(() => {
    if (page > 1 && timeBlockPageKey === '') return;
    fetchTimeBlocks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    pageSize,
    ascending,
    teacherId,
    subjectId,
    activeOnly,
    timeBlockPageKey,
  ]);

  const fetchTimeBlocks = async () => {
    setTimeBlockList((prev) => ({ ...prev, isLoading: true }));

    const setEmptyState = () => {
      setTimeBlockList((prev) => ({
        ...prev,
        rows: [],
        count: 0,
        isLoading: false,
        nextPaginationKey: null,
        total: lastKnownTotal || DEFAULT_TOTAL,
      }));
    };

    try {
      const data = await getV2TimeBlocks({
        page,
        pageSize,
        ascending,
        teacherId,
        subjectId,
        activeOnly,
        paginationKey: timeBlockPageKey,
      });

      if (!data?.items || data?.items?.length === 0) {
        setEmptyState();
        return;
      }

      const rows = getTimeBlockV2ListData(data);

      if (data?.nextPaginationKey) {
        dispatch(setTimeBlockV2PaginationKey(data.nextPaginationKey));
      }

      const isLastPage = data.nextPaginationKey === null;
      const updatedTotal = isLastPage
        ? (page - 1) * pageSize + rows.length
        : DEFAULT_TOTAL;

      if (isLastPage) {
        setLastKnownTotal(updatedTotal);
      }

      setTimeBlockList((prev) => ({
        ...prev,
        rows,
        isLoading: false,
        count: data?.count,
        total: updatedTotal,
        nextPaginationKey: data?.nextPaginationKey,
        currentPaginationKey: data?.currentPaginationKey,
      }));
    } catch (error) {
      setEmptyState();
    }
  };

  const handlePageChange = (newPage) => {
    const goingForward = newPage + 1 > page;

    if (goingForward && !nextPaginationKey) return;

    setTimeBlockList((prev) => ({ ...prev, page: newPage + 1 }));

    const paginationIdValue = {
      id: newPage,
      value: nextPaginationKey,
    };

    const existingEntry = timeBlocksV2PaginationData?.find(
      (i) => i.id === newPage,
    );

    if (existingEntry) {
      dispatch(setTimeBlockV2PreviousKey(existingEntry.value));
      setTimeBlockPageKey(existingEntry.value);
    } else {
      dispatch(setTimeBlockV2PaginationData(paginationIdValue));
      setTimeBlockPageKey(nextPaginationKey);
    }

    // Going back to first page
    if (newPage < 1) {
      resetPagination();
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    resetPagination(newPageSize);
  };

  const handleClearFilters = () => {
    reset(DEFAULT_FORM_VALUES);
  };

  const handleView = (params) => {
    console.log('handleView', params.row);
  };

  const handleDelete = (params) => {
    console.log('handleDelete', params.row);
  };

  const handleNewTimeBlock = () => {
    console.log('handleNewTimeBlock');
    setOpenTimeBlockDialog(true);
  };

  return (
    <>
      <MDBox mb={4.5} />
      <GetTimeBlockHeader handleNewTimeBlock={handleNewTimeBlock} />

      <TimeBlockFilters
        control={control}
        onClear={handleClearFilters}
        teachers={availableUsersData}
        booleanOptions={booleanOptions}
        subjects={availableSubjectsData}
      />

      <DeltaDataGrid
        listData={timeBlockList}
        setListData={handlePageSizeChange}
        handelPageChange={handlePageChange}
        columns={getTimeBlockV2Columns(handleView, handleDelete)}
      />

      <ManageTimeBlockDialog
        open={openTimeBlockDialog}
        setOpen={setOpenTimeBlockDialog}
        fetchTimeBlocks={fetchTimeBlocks}
      />
    </>
  );
};

export default ManageTimeBlocksTabV2;
